import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import "@/i18n";

import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import ServiceAreas from "@/pages/public/ServiceAreas";
import Quote from "@/pages/public/Quote";
import Book from "@/pages/public/Book";
import BookingSuccess from "@/pages/public/BookingSuccess";
import BookingCancel from "@/pages/public/BookingCancel";
import FAQ from "@/pages/public/FAQ";
import Checklist from "@/pages/public/Checklist";
import Contact from "@/pages/public/Contact";
import Terms from "@/pages/public/Terms";
import Privacy from "@/pages/public/Privacy";
import BlogIndex from "@/pages/public/BlogIndex";
import BlogPost from "@/pages/public/BlogPost";
import RivieraHome from "@/pages/riviera/RivieraHome";
import FrLegalPage from "@/pages/riviera/FrLegalPage";
import NlCity from "@/pages/public/NlCity";
import RivieraCity from "@/pages/riviera/RivieraCity";
import RivieraInquiry from "@/pages/riviera/RivieraInquiry";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import Dashboard from "@/pages/admin/Dashboard";
import Leads from "@/pages/admin/Leads";
import AdminBookings from "@/pages/admin/Bookings";
import Customers from "@/pages/admin/Customers";
import AdminRevenue from "@/pages/admin/Revenue";
import Cleaners from "@/pages/admin/Cleaners";
import Jobs from "@/pages/admin/Jobs";
import Schedule from "@/pages/admin/Schedule";
import Payments from "@/pages/admin/Payments";
import Complaints from "@/pages/admin/Complaints";
import Templates from "@/pages/admin/Templates";
import DiscountLeads from "@/pages/admin/DiscountLeads";
import AdminSettings from "@/pages/admin/Settings";

import MyAccount from "@/pages/customer/MyAccount";

import ProviderPortal from "@/pages/provider/Portal";

import DiscountPopup from "@/components/public/DiscountPopup";
import Unsubscribe from "@/pages/public/Unsubscribe";

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role && user.role !== "admin") {
    // route role mismatch: redirect to the user's natural home
    const home = user.role === "customer" ? "/my-account" : user.role === "provider" ? "/portal" : "/admin";
    return <Navigate to={home} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <DiscountPopup />
        <Routes>
          {/* Public */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/service-areas" element={<ServiceAreas />} />
            <Route path="/service-areas/:city" element={<NlCity />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/book" element={<Book />} />
            <Route path="/booking-success" element={<BookingSuccess />} />
            <Route path="/booking-cancel" element={<BookingCancel />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/checklist" element={<Checklist />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/fr" element={<RivieraHome />} />
            <Route path="/fr/inquiry" element={<RivieraInquiry />} />
            <Route path="/fr/book" element={<Book key="fr" country="FR" />} />
            <Route path="/fr/mentions-legales" element={<FrLegalPage docKey="mentions-legales" />} />
            <Route path="/fr/conditions-generales" element={<FrLegalPage docKey="conditions-generales" />} />
            <Route path="/fr/confidentialite" element={<FrLegalPage docKey="confidentialite" />} />
            <Route path="/fr/cookies" element={<FrLegalPage docKey="cookies" />} />
            <Route path="/fr/retractation" element={<FrLegalPage docKey="retractation" />} />
            <Route path="/fr/:city" element={<RivieraCity />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<ResetPassword />} />

          {/* Customer portal */}
          <Route
            path="/my-account"
            element={
              <RequireAuth role="customer">
                <MyAccount />
              </RequireAuth>
            }
          />

          {/* Provider portal */}
          <Route
            path="/portal"
            element={
              <RequireAuth role="provider">
                <ProviderPortal />
              </RequireAuth>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <RequireAuth role="admin">
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="customers" element={<Customers />} />
            <Route path="cleaners" element={<Cleaners />} />
            <Route path="jobs" element={<Navigate to="/admin/bookings" replace />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="payments" element={<Payments />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="complaints" element={<Complaints />} />
            <Route path="templates" element={<Templates />} />
            <Route path="discount-leads" element={<DiscountLeads />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
